import { AntInput } from "@/CommonElements/AntInput";
import { AntSelect } from "@/CommonElements/AntSelect";
import { Controllers } from "@/CommonElements/Controller";
import { PageHeader } from "@/CommonElements/PageTitle";
import { ActionsFooter } from "@/Component/ActionsFooter";
import { Button, Stack } from "react-bootstrap";
import { useForm, FormProvider, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router";

import { Container, Spinner } from "reactstrap";
import { cn, PickKeyPairs, addQToItem } from "@/utils/helpers";
import { Switch, Tag } from "antd";
import {
  useCreatePeriodCodeMutation,
  useGetOnePeriodCodeQuery,
  useUpdatePeriodCodeMutation,
  useExaminationPeriodsQuery,
} from "@/services/basicData";
import { useAllAssignedInspectionUsers } from "@/services/houseInspections";
import { useLocalizedText } from "@/utils/hooks/useLocalizedText";
import { useEffect, useMemo } from "react";
import dayjs from "dayjs";
import { TIME_FORMAT, TIME_FORMAT_12_HOURS } from "@/utils/constants";
import { CountModal } from "@/Component/CountModal";
import { LabelValue } from "@/Component/Value";

export const PeriodCodePage = () => {
  // ==============
  // hooks
  // ==============
  const { t } = useTranslation();
  const { mode } = useParams();
  const isViewMode = mode === "view";

  const form = useForm({
    defaultValues: {
      is_active: true,
      user_ids: [],
    },
  });
  const {
    control,
    formState: { errors },
  } = form;

  const { handleSubmit, setError } = form;

  // ==============
  // Side Effects
  // =============

  const { mutate: createPeriodCode, isPending: isCreatePeriodCodePending } =
    useCreatePeriodCodeMutation(setError);
  const { mutate: updatePeriodCode, isPending: isUpdatePeriodCodePending } =
    useUpdatePeriodCodeMutation(setError);

  const onSubmit = (data) => {
    if (mode === "create") {
      createPeriodCode(data, {
        onSuccess: () => {
          form.reset(form.formState.defaultValues);
        },
      });
    } else {
      updatePeriodCode(data, {
        onSuccess: () => {
          form.reset(form.formState.defaultValues);
        },
      });
    }
  };

  const isPending = isCreatePeriodCodePending || isUpdatePeriodCodePending;
  const backLink =
    window.history.length > 1 ? -1 : `/app/makkah/basic-data/period_codes`;
  const title = {
    view: t("VIEW_PERIOD_CODE"),
    create: t("CREATE_PERIOD_CODE"),
    edit: t("UPDATE_PERIOD_CODE"),
  };

  return (
    <Stack gap={2}>
      <PageHeader title={title[mode]}></PageHeader>
      <Container fluid>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit(onSubmit)}>
            {/* Form */}
            <PeriodCodeForm control={control} errors={errors} />

            {/* Form Actions */}
            {isViewMode ? null : (
              <ActionsFooter>
                <Button
                  className="btn btn-primary"
                  type="submit"
                  disabled={isPending}
                >
                  {isPending ? <Spinner size="sm" /> : null}
                  {mode === "create" ? t("CREATE") : t("UPDATE")}
                </Button>
                <Link
                  disabled={isPending}
                  style={{ pointerEvents: isPending ? "none" : "auto" }}
                  to={backLink}
                  className="btn btn-danger"
                  type="submit"
                >
                  {t("CANCEL")}
                </Link>
              </ActionsFooter>
            )}
          </form>
        </FormProvider>
      </Container>
    </Stack>
  );
};

const PeriodCodeForm = () => {
  // ==============
  // hooks
  // ==============
  const form = useFormContext();
  const { id, mode } = useParams();
  const isViewMode = mode === "view";
  const { getLocalizedText } = useLocalizedText();


  // ==============
  // Side Effects
  // ==============
  const { data: periodCodeResponse, isLoading } = useGetOnePeriodCodeQuery({
    id,
  });
  const periodCode = periodCodeResponse?.data?.data;

  // Fetch examination periods
  const {
    data: examinationPeriodsRes,
    isLoading: isLoadingExaminationPeriods,
  } = useExaminationPeriodsQuery({});
  const examinationPeriods = examinationPeriodsRes?.data?.data ?? [];

  // Fetch users
  const { data: usersRes, isLoading: isLoadingUsers } =
    useAllAssignedInspectionUsers({ city: "makkah" });
  const users = usersRes?.data?.data ?? [];

  useEffect(() => {
    if (periodCode?.period_code) {
      const { assigned_users, ...pickedData } = PickKeyPairs(
        periodCode.period_code,
        ["code", "examination_period_id", "assigned_users", "is_active"]
      );
      form.reset({
        ...pickedData,
        user_ids: assigned_users.map((user) => user.id),
      });
    }
  }, [periodCode]);

  const {
    control,
    formState: { errors },
  } = form;
  const { t } = useTranslation();

  // Format examination period options
  const examinationPeriodOptions = useMemo(() => {
    return examinationPeriods.map(addQToItem).map((period) => {
      const name = getLocalizedText(period.name_ar, period.name_en);
      const timeFrom = period.time_from
        ? dayjs(period.time_from, TIME_FORMAT).format(TIME_FORMAT_12_HOURS)
        : "";
      const timeTo = period.time_to
        ? dayjs(period.time_to, TIME_FORMAT).format(TIME_FORMAT_12_HOURS)
        : "";
      const timeRange = timeFrom && timeTo ? `(${timeFrom} - ${timeTo})` : "";
      const label = timeRange ? `${name} - ${timeRange}` : name;

      return {
        q: period.q,
        value: period.id,
        label: label,
      };
    });
  }, [examinationPeriods, getLocalizedText]);

  // Format user options
  const userOptions = useMemo(() => {
    return users.map(addQToItem).map((user) => ({
      q: user.q,
      value: user.id,
      label: getLocalizedText(user.name_ar, user.name_en),
    }));
  }, [users, getLocalizedText]);

  const controllers = [
    {
      labelProps: {
        children: t("CODE"),
      },
      controllerProps: {
        name: "code",
        rules: {
          required: t("FIELD_REQUIRED"),
        },
        render: ({ field }) => (
          <AntInput
            {...field}
            placeholder={t("CODE")}
            status={errors.code ? "error" : undefined}
          />
        ),
      },
      renderValue: (value) => value || "-",
    },
    {
      labelProps: {
        children: t("EXAMINATION_PERIOD"),
      },
      controllerProps: {
        name: "examination_period_id",
        rules: {
          required: t("FIELD_REQUIRED"),
        },
        render: ({ field }) => (
          <AntSelect
            {...field}
            allowClear
            optionFilterProp="q"
            showSearch
            loading={isLoadingExaminationPeriods}
            options={examinationPeriodOptions}
            status={errors.examination_period_id ? "error" : undefined}
            placeholder={t("EXAMINATION_PERIOD")}
          />
        ),
      },
      renderValue: (value) => {
        const period = examinationPeriods.find((p) => p.id === value);
        if (!period) return "-";
        const name = getLocalizedText(period.name_ar, period.name_en);
        const timeFrom = period.time_from
          ? dayjs(period.time_from, TIME_FORMAT).format(TIME_FORMAT_12_HOURS)
          : "";
        const timeTo = period.time_to
          ? dayjs(period.time_to, TIME_FORMAT).format(TIME_FORMAT_12_HOURS)
          : "";
        const timeRange = timeFrom && timeTo ? `(${timeFrom} - ${timeTo})` : "";
        return timeRange ? `${name} - ${timeRange}` : name;
      },
    },
    {
      labelProps: {
        children: t("ASSIGNED_USERS"),
      },
      controllerProps: {
        name: "user_ids",
        rules: {
          required: t("FIELD_REQUIRED"),
        },
        render: ({ field }) => (
          <>
            <style>{`
  .ant-select-selector * {
    min-height: 48px !important;
    display: flex !important;
    align-items: center !important;
  }
`}</style>
            <AntSelect
              {...field}
              mode="multiple"
              allowClear
              optionFilterProp="q"
              maxTagCount="responsive"
              showSearch
              loading={isLoadingUsers}
              options={userOptions}
              status={errors.user_ids ? "error" : undefined}
              placeholder={t("ASSIGNED_USERS")}
            />
          </>
        ),
      },
      renderValue: (value) => (
        <CountModal
          modal={{
            header: {
              children: t("ITEMS_COUNT"),
            },
            body: {
              children: (
                <ul className="p-0 d-flex flex-column gap-2">
                  {periodCode?.period_code?.assigned_users?.map((user) => (
                    <li key={user.id} className="border py-2 px-2 rounded-2">
                      <Link
                        to={`/app/user_management/users/${user.id}/profile`}
                      >
                        <LabelValue.Value
                          value={getLocalizedText(user.name_ar, user.name_en)}
                        />
                      </Link>
                    </li>
                  ))}
                </ul>
              ),
            },
            props: {},
          }}
        >
          {periodCode?.period_code?.assigned_users?.length || "-"}
        </CountModal>
      ),
    },
    {
      labelProps: {
        children: t("STATUS"),
      },
      controllerProps: {
        name: "is_active",
        render: ({ field }) => (
          <div>
            <Switch
              // {...field}
              checked={field.value}
              onChange={(checked) => field.onChange(checked)}
              checkedChildren={t("ACTIVE")}
              unCheckedChildren={t("INACTIVE")}
            />
          </div>
        ),
      },
      renderValue: (value) => (
        <Tag className={value ? "badge-success" : "badge-danger"} color={value ? "success" : "danger"}>
          {value ? t("ACTIVE") : t("INACTIVE")}
        </Tag>
      ),
    },
  ];

  return (
    <Controllers
      isLoading={isLoading}
      isViewMode={isViewMode}
      watch={form.watch}
      {...{
        controllers: controllers.map((config) => ({
          ...config,
          labelProps: {
            ...config.labelProps,
            className: cn("fw-bold required", config.labelProps.className),
          },
          controllerProps: {
            ...config.controllerProps,
            rules: {
              ...(config.controllerProps.rules || {}),
            },
            control,
            errors,
          },
          layout: {
            colProps: { xs: 6, md: 6 },
          },
        })),
      }}
    />
  );
};
